import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import DiscrepancyAlert from "../components/DiscrepancyAlert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Users,
  Building2,
  FileText,
  ClipboardCheck,
  TrendingUp,
  Calendar,
  Plus,
  ArrowRight,
  Clock,
  Bell,
  Car,
  Package,
  Receipt,
  UserPlus,
  FileSignature,
  CalendarPlus,
  Zap,
  Pencil,
  X,
  Check,
  GripVertical,
  Eye,
  EyeOff,
  Palette,
  Sun,
  Moon,
  Monitor,
  Settings2,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Save,
  Layout,
  UserCog,
  LogOut,
  Mail,
  DollarSign,
  Shield,
  Truck,
  Briefcase,
  GraduationCap,
  BarChart3,
  FolderOpen,
  Heart,
  Inbox,
  ExternalLink,
  Settings,
  RefreshCw,
  Sparkles,
  Wallet,
  BookOpen,
  PiggyBank,
  LayoutDashboard,
} from "lucide-react";
import axios from "axios";
import { formatCurrency, getStatusClass, formatDate } from "../lib/utils";
import ManagedPortalUsersWidget from "../components/portal/ManagedPortalUsersWidget";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Phase 19.10 refactor: bulky pure-data constants moved to ./dashboard/constants.js
import {
  DEFAULT_WIDGETS,
  ACCENT_COLORS,
  AVAILABLE_ICONS,
  AVAILABLE_DESTINATIONS,
  BUTTON_COLORS,
  SUGGESTED_ACTIONS,
  STAT_CARDS_CATALOG,
  getDefaultStatCards,
  HIGHLIGHT_PACKS,
  getDefaultQuickActions,
  STARTER_PACKS,
} from "./dashboard/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const Dashboard = () => {
  const { user, isOfficeManager, isQuoter, previewMode, startPreview, exitPreview, canPreview, effectiveRole, isPlatformAdminMode } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  // Phase 19.27 — Platform admins land on the Owner Console by default. They can
  // flip the "View as Customer" toggle in /admin to see this customer dashboard.
  useEffect(() => {
    if (isPlatformAdminMode) {
      navigate("/admin", { replace: true });
    }
  }, [isPlatformAdminMode, navigate]);
  
  // Dashboard data
  const [stats, setStats] = useState({});
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [arSummary, setArSummary] = useState(null);
  const [apSummary, setApSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [designDialogOpen, setDesignDialogOpen] = useState(false);
  
  // Preview mode dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  
  // Quick action edit dialog
  const [quickActionDialogOpen, setQuickActionDialogOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null); // null = adding new
  const [actionForm, setActionForm] = useState({
    label: "",
    iconKey: "Plus",
    href: "/dashboard",
    action: null,
    color: "bg-emerald-500 hover:bg-emerald-600",
  });
  
  // Widget configuration
  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem(`dashboard_widgets_${user?.id}`);
    if (!saved) return DEFAULT_WIDGETS;
    try {
      // Phase 15a: merge saved widgets with defaults so newly-added widget keys
      // (financialSnapshot, emailPreview, etc.) auto-appear for users who already
      // have ui_settings saved. Saved enabled/order overrides defaults; new keys
      // come in enabled per default config.
      const parsed = JSON.parse(saved);
      const merged = { ...DEFAULT_WIDGETS };
      for (const k of Object.keys(parsed)) {
        if (merged[k]) merged[k] = { ...merged[k], ...parsed[k] };
      }
      return merged;
    } catch {
      return DEFAULT_WIDGETS;
    }
  });
  
  // Custom quick actions — defaults until the user makes a change, then persisted in DB.
  // localStorage cache provides instant render before the API responds.
  const [quickActions, setQuickActions] = useState(() => {
    const saved = localStorage.getItem(`dashboard_quick_actions_${user?.id}`);
    if (saved) {
      try { return JSON.parse(saved); } catch { /* fall through */ }
    }
    return getDefaultQuickActions(isOfficeManager, isQuoter);
  });
  // Drag-and-drop reorder
  const [dragIndex, setDragIndex] = useState(null);
  // Phase 18.2: customizable KPI stat cards
  const [statCards, setStatCards] = useState(() => getDefaultStatCards(isOfficeManager, isQuoter));
  const [statDragIndex, setStatDragIndex] = useState(null);
  const [addStatDialogOpen, setAddStatDialogOpen] = useState(false);
  const [highlightPackDialogOpen, setHighlightPackDialogOpen] = useState(false);
  // Phase 18.2: drag-rearrange for the larger widgets (Calendar / Financial / Email…)
  const [widgetDragId, setWidgetDragId] = useState(null);

  const saveStatCards = useCallback((newCards) => {
    setStatCards(newCards);
    localStorage.setItem(`dashboard_stat_cards_${user?.id}`, JSON.stringify(newCards));
    axios.put(`${API}/dashboard/stat-cards`, { stat_cards: newCards })
      .catch((e) => console.warn("Stat-cards sync failed:", e?.response?.status));
  }, [user?.id]);

  const resetStatCards = () => {
    const defaults = getDefaultStatCards(isOfficeManager, isQuoter);
    setStatCards(defaults);
    localStorage.removeItem(`dashboard_stat_cards_${user?.id}`);
    axios.delete(`${API}/dashboard/stat-cards`).catch(() => {});
    toast.success("Highlight cards reset to defaults");
  };

  const removeStatCard = (id) => {
    saveStatCards(statCards.filter((s) => s !== id));
  };

  const addStatCard = (id) => {
    if (statCards.includes(id)) return;
    saveStatCards([...statCards, id]);
    setAddStatDialogOpen(false);
    toast.success(`Added "${STAT_CARDS_CATALOG[id]?.label || id}"`);
  };

  // Phase 18.3: apply a Smart Highlights pack — MERGE with existing, never replace
  const applyHighlightPack = (packId) => {
    const pack = HIGHLIGHT_PACKS[packId];
    if (!pack) return;
    const myRole = isOfficeManager ? "office_manager" : isQuoter ? "quoter" : "employee";
    // Filter pack to cards available to this role + not already present
    const toAdd = pack.cards.filter((id) => {
      const def = STAT_CARDS_CATALOG[id];
      return def && def.roles.includes(myRole) && !statCards.includes(id);
    });
    if (toAdd.length === 0) {
      toast.info(`All "${pack.name}" cards are already on your dashboard`);
      setHighlightPackDialogOpen(false);
      return;
    }
    saveStatCards([...statCards, ...toAdd]);
    setHighlightPackDialogOpen(false);
    toast.success(`Added ${toAdd.length} card${toAdd.length > 1 ? "s" : ""} from "${pack.name}" pack`);
  };

  // Phase 18: Smart Starter Pack picker
  const [starterPackDialogOpen, setStarterPackDialogOpen] = useState(false);
  const applyStarterPack = (packId) => {
    const pack = STARTER_PACKS[packId];
    if (!pack) return;
    // Clone so we don't mutate the catalog and so each pack apply produces fresh IDs
    const fresh = pack.actions.map((a, idx) => ({ ...a, id: `${packId}-${Date.now()}-${idx}` }));
    saveQuickActions(fresh);
    setStarterPackDialogOpen(false);
    toast.success(`Applied "${pack.name}" starter pack`);
  };

  // Phase 19: Persona Templates (applies BOTH Quick Actions + Highlight stat cards)
  const [personaDialogOpen, setPersonaDialogOpen] = useState(false);
  const [personaTemplates, setPersonaTemplates] = useState([]);
  const [customPresets, setCustomPresets] = useState([]);
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");

  const reloadPersonaData = useCallback(() => {
    axios.get(`${API}/dashboard/persona-templates`)
      .then((res) => setPersonaTemplates(res.data?.templates || []))
      .catch(() => {});
    axios.get(`${API}/dashboard/custom-presets`)
      .then((res) => setCustomPresets(res.data?.presets || []))
      .catch(() => {});
  }, []);

  const applyPersonaTemplate = async (persona) => {
    try {
      const res = await axios.post(`${API}/dashboard/persona-templates/${persona.key}/apply`);
      const qa = res.data?.quick_actions || [];
      const sc = res.data?.stat_cards || [];
      setQuickActions(qa);
      setStatCards(sc);
      localStorage.setItem(`dashboard_quick_actions_${user?.id}`, JSON.stringify(qa));
      localStorage.setItem(`dashboard_stat_cards_${user?.id}`, JSON.stringify(sc));
      setPersonaDialogOpen(false);
      toast.success(`Applied "${persona.name}" persona`);
    } catch (e) {
      toast.error("Failed to apply persona");
    }
  };

  const applyCustomPreset = async (preset) => {
    try {
      await axios.post(`${API}/dashboard/custom-presets/${preset.id}/apply`);
      const qa = preset.quick_actions || [];
      const sc = preset.stat_cards || [];
      setQuickActions(qa);
      setStatCards(sc);
      localStorage.setItem(`dashboard_quick_actions_${user?.id}`, JSON.stringify(qa));
      localStorage.setItem(`dashboard_stat_cards_${user?.id}`, JSON.stringify(sc));
      setPersonaDialogOpen(false);
      toast.success(`Applied "${preset.name}"`);
    } catch (e) {
      toast.error("Failed to apply preset");
    }
  };

  const deleteCustomPreset = async (preset) => {
    if (!window.confirm(`Delete preset "${preset.name}"?`)) return;
    try {
      await axios.delete(`${API}/dashboard/custom-presets/${preset.id}`);
      setCustomPresets((prev) => prev.filter((p) => p.id !== preset.id));
      toast.success("Preset deleted");
    } catch (e) {
      toast.error("Failed to delete preset");
    }
  };

  const saveCurrentAsPreset = async () => {
    const name = newPresetName.trim();
    if (!name) return;
    try {
      const res = await axios.post(`${API}/dashboard/custom-presets`, {
        name,
        quick_actions: quickActions,
        stat_cards: statCards,
      });
      setCustomPresets((prev) => [res.data, ...prev]);
      setSavePresetDialogOpen(false);
      setNewPresetName("");
      toast.success(`Saved "${name}" preset`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to save preset";
      toast.error(detail);
    }
  };
  
  // Design preferences
  const [designPrefs, setDesignPrefs] = useState(() => {
    const saved = localStorage.getItem(`dashboard_design_${user?.id}`);
    return saved ? JSON.parse(saved) : {
      accentColor: "#10b981",
      compactMode: false,
      showWelcome: true,
      cardStyle: "default", // default, minimal, bordered
    };
  });

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchUISettings();
    // Phase 18: pull saved Quick Actions from backend so they roam across devices
    axios.get(`${API}/dashboard/quick-actions`)
      .then((res) => {
        const list = res.data?.quick_actions || [];
        if (list.length > 0) {
          setQuickActions(list);
          localStorage.setItem(`dashboard_quick_actions_${user?.id}`, JSON.stringify(list));
        }
        // If empty → keep showing role-based defaults (already in state)
      })
      .catch(() => { /* offline / unauth — keep defaults */ });
    // Phase 18.2: hydrate stat cards too
    axios.get(`${API}/dashboard/stat-cards`)
      .then((res) => {
        const list = res.data?.stat_cards || [];
        if (list.length > 0) {
          setStatCards(list);
          localStorage.setItem(`dashboard_stat_cards_${user?.id}`, JSON.stringify(list));
        }
      })
      .catch(() => {});
    // Phase 19: load persona templates + saved custom presets
    reloadPersonaData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply accent color to CSS variables
  useEffect(() => {
    document.documentElement.style.setProperty('--accent-custom', designPrefs.accentColor);
  }, [designPrefs.accentColor]);

  const fetchUISettings = async () => {
    try {
      const response = await axios.get(`${API}/ui-settings`);
      const data = response.data;
      
      if (data.dashboard_widgets) {
        // Phase 15a merge — same logic as the localStorage path
        setWidgets((prev) => {
          const merged = { ...DEFAULT_WIDGETS };
          for (const k of Object.keys(data.dashboard_widgets)) {
            if (merged[k]) merged[k] = { ...merged[k], ...data.dashboard_widgets[k] };
          }
          return merged;
        });
      }
      // Phase 18: Quick Actions are now persisted via /api/dashboard/quick-actions only.
      // Intentionally ignore data.quick_actions from legacy ui_settings so old "Test Action"
      // entries don't shadow the role-based defaults.
      if (data.accent_color) {
        setDesignPrefs(prev => ({ ...prev, accentColor: data.accent_color }));
      }
    } catch (error) {
      console.log("Using local settings (backend unavailable)");
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [statsRes, quotesRes, schedulesRes, notifRes, arRes, apRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/quotes`),
        axios.get(`${API}/schedules`).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications`).catch(() => ({ data: [] })),
        axios.get(`${API}/accounts/receivable/summary`).catch(() => ({ data: null })),
        axios.get(`${API}/accounts/payable/summary/stats`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setRecentQuotes(quotesRes.data.slice(0, 5));
      setArSummary(arRes.data);
      setApSummary(apRes.data);
      
      const allSchedules = schedulesRes.data || [];
      setCalendarEvents(allSchedules);
      
      // Get upcoming jobs (next 7 days)
      const today = new Date();
      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      const upcoming = allSchedules.filter(s => {
        const jobDate = new Date(s.confirmed_date || s.proposed_dates?.[0]);
        return jobDate >= today && jobDate <= nextWeek;
      }).slice(0, 5);
      setUpcomingJobs(upcoming);
      
      const notifData = notifRes.data;
      setNotifications(Array.isArray(notifData) ? notifData.slice(0, 8) : []);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettingsToBackend = useCallback(async (newWidgets, newActions, newAccentColor) => {
    try {
      await axios.put(`${API}/ui-settings`, {
        dashboard_widgets: newWidgets || widgets,
        quick_actions: newActions || quickActions,
        accent_color: newAccentColor || designPrefs.accentColor,
      });
    } catch (error) {
      console.log("Failed to save to backend, using localStorage fallback");
    }
  }, [widgets, quickActions, designPrefs.accentColor]);

  const saveWidgets = useCallback((newWidgets) => {
    setWidgets(newWidgets);
    localStorage.setItem(`dashboard_widgets_${user?.id}`, JSON.stringify(newWidgets));
    saveSettingsToBackend(newWidgets, null, null);
  }, [user?.id, saveSettingsToBackend]);

  const saveDesignPrefs = useCallback((newPrefs) => {
    setDesignPrefs(newPrefs);
    localStorage.setItem(`dashboard_design_${user?.id}`, JSON.stringify(newPrefs));
    saveSettingsToBackend(null, null, newPrefs.accentColor);
  }, [user?.id, saveSettingsToBackend]);

  const saveQuickActions = useCallback((newActions) => {
    setQuickActions(newActions);
    localStorage.setItem(`dashboard_quick_actions_${user?.id}`, JSON.stringify(newActions));
    // Phase 18: persist to backend (single source of truth — no more legacy ui_settings sync)
    axios.put(`${API}/dashboard/quick-actions`, { quick_actions: newActions })
      .catch((e) => console.warn("Quick actions sync to backend failed:", e?.response?.status));
  }, [user?.id]);

  const toggleWidget = (widgetId) => {
    const newWidgets = {
      ...widgets,
      [widgetId]: { ...widgets[widgetId], enabled: !widgets[widgetId].enabled }
    };
    saveWidgets(newWidgets);
  };

  const moveWidget = (widgetId, direction) => {
    const widgetList = Object.values(widgets).sort((a, b) => a.order - b.order);
    const currentIndex = widgetList.findIndex(w => w.id === widgetId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= widgetList.length) return;
    
    const newWidgets = { ...widgets };
    const currentOrder = widgets[widgetId].order;
    const swapWidget = widgetList[newIndex];
    
    newWidgets[widgetId] = { ...newWidgets[widgetId], order: swapWidget.order };
    newWidgets[swapWidget.id] = { ...newWidgets[swapWidget.id], order: currentOrder };
    
    saveWidgets(newWidgets);
  };

  const resetToDefaults = () => {
    saveWidgets(DEFAULT_WIDGETS);
    saveDesignPrefs({
      accentColor: "#10b981",
      compactMode: false,
      showWelcome: true,
      cardStyle: "default",
    });
    saveQuickActions(getDefaultQuickActions(isOfficeManager, isQuoter));
  };

  // Quick Action management functions
  const openAddActionDialog = () => {
    setEditingAction(null);
    setActionForm({
      label: "",
      iconKey: "Plus",
      href: "",
      action: null,
      color: "bg-emerald-500 hover:bg-emerald-600",
    });
    setQuickActionDialogOpen(true);
  };

  const applySuggestion = (s) => {
    setActionForm({
      label: s.label,
      iconKey: s.iconKey,
      href: s.href,
      action: s.action || null,
      color: s.color,
    });
  };

  const openEditActionDialog = (action) => {
    setEditingAction(action);
    setActionForm({
      label: action.label,
      iconKey: action.iconKey,
      href: action.href,
      action: action.action,
      color: action.color,
    });
    setQuickActionDialogOpen(true);
  };

  const saveActionForm = () => {
    const label = (actionForm.label || "").trim();
    const href = (actionForm.href || "").trim();
    if (!label) {
      toast.error("Please enter a label for the action");
      return;
    }
    if (!href || href === "#" || href === "/dashboard") {
      toast.error("Please choose where the action should go (it can't be left as Dashboard)");
      return;
    }

    if (editingAction) {
      // Update existing action
      const newActions = quickActions.map(a =>
        a.id === editingAction.id ? { ...a, ...actionForm, label, href } : a
      );
      saveQuickActions(newActions);
      toast.success(`Updated "${label}"`);
    } else {
      // Add new action
      const newAction = {
        id: Date.now().toString(),
        ...actionForm,
        label,
        href,
      };
      saveQuickActions([...quickActions, newAction]);
      toast.success(`Added "${label}"`);
    }
    setQuickActionDialogOpen(false);
  };

  const resetQuickActionsToDefaults = () => {
    const defaults = getDefaultQuickActions(isOfficeManager, isQuoter);
    setQuickActions(defaults);
    localStorage.removeItem(`dashboard_quick_actions_${user?.id}`);
    // Phase 18: clear DB row so the user truly returns to "automatic main setting"
    axios.delete(`${API}/dashboard/quick-actions`).catch(() => {});
    toast.success("Quick Actions reset to defaults");
  };

  const removeQuickAction = (actionId) => {
    const newActions = quickActions.filter(a => a.id !== actionId);
    saveQuickActions(newActions);
  };

  const moveQuickAction = (actionId, direction) => {
    const currentIndex = quickActions.findIndex(a => a.id === actionId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= quickActions.length) return;
    
    const newActions = [...quickActions];
    [newActions[currentIndex], newActions[newIndex]] = [newActions[newIndex], newActions[currentIndex]];
    saveQuickActions(newActions);
  };

  const handleQuickAction = (action) => {
    if (action.action) {
      sessionStorage.setItem('quickAction', action.action);
    }
    navigate(action.href);
  };

  // Get sorted widgets
  const sortedWidgets = Object.values(widgets).sort((a, b) => a.order - b.order);

  // Widget render functions
  const renderQuickActions = () => (
    <Card className={`border-2 border-dashed border-primary/20 bg-gradient-to-br from-primary/5 to-transparent ${editMode ? 'ring-2 ring-primary/50' : ''}`} data-testid="quick-actions-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" />
          Quick Actions
        </CardTitle>
        <div className="flex items-center gap-1">
          {/* Always-visible customization controls — no need to enter "Edit Dashboard" mode */}
          <Button
            variant="ghost"
            size="sm"
            onClick={openAddActionDialog}
            className="text-xs gap-1 h-8"
            data-testid="quick-action-add-btn"
            title="Add a new Quick Action"
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Add</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="quick-action-menu-btn" title="Customize">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditMode(true)} data-testid="quick-action-edit-mode">
                <Pencil className="h-4 w-4 mr-2" /> Edit / Reorder
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStarterPackDialogOpen(true)} data-testid="quick-action-starter-packs">
                <Sparkles className="h-4 w-4 mr-2" /> Choose Starter Pack
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { reloadPersonaData(); setPersonaDialogOpen(true); }} data-testid="persona-templates-btn">
                <Layout className="h-4 w-4 mr-2" /> Persona Templates &amp; Presets
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setNewPresetName(""); setSavePresetDialogOpen(true); }} data-testid="save-preset-btn">
                <Save className="h-4 w-4 mr-2" /> Save as Custom Preset…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={resetQuickActionsToDefaults} data-testid="quick-action-reset">
                <RefreshCw className="h-4 w-4 mr-2" /> Reset to Defaults
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => toggleWidget('quickActions')} data-testid="quick-action-hide-widget">
                <EyeOff className="h-4 w-4 mr-2" /> Hide Widget
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {editMode && (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('quickActions', 'up')}>
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('quickActions', 'down')}>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {quickActions.map((action, index) => {
            const IconComponent = AVAILABLE_ICONS[action.iconKey]?.icon || Plus;
            const isDragging = dragIndex === index;
            return (
              <div
                key={action.id}
                className={`relative group ${isDragging ? 'opacity-40' : ''}`}
                draggable
                onDragStart={(e) => {
                  setDragIndex(index);
                  e.dataTransfer.effectAllowed = "move";
                  // Firefox needs data on the transfer
                  try { e.dataTransfer.setData("text/plain", action.id); } catch { /* ignore */ }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex === null || dragIndex === index) return;
                  const newList = [...quickActions];
                  const [moved] = newList.splice(dragIndex, 1);
                  newList.splice(index, 0, moved);
                  saveQuickActions(newList);
                  setDragIndex(null);
                }}
                onDragEnd={() => setDragIndex(null)}
                data-testid={`quick-action-tile-${action.id}`}
              >
                {/* Always-visible per-tile controls on hover (no need for Edit mode) */}
                <div className="absolute -top-2 -right-2 z-10 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-6 w-6 rounded-full shadow-md cursor-grab active:cursor-grabbing"
                    title="Drag to reorder"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <GripVertical className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-6 w-6 rounded-full shadow-md"
                    onClick={(e) => { e.stopPropagation(); openEditActionDialog(action); }}
                    title="Edit"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-6 w-6 rounded-full shadow-md"
                    onClick={(e) => { e.stopPropagation(); removeQuickAction(action.id); }}
                    title="Remove"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <button
                  onClick={() => handleQuickAction(action)}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl ${action.color} text-white transition-all duration-200 hover:scale-105 hover:shadow-lg w-full h-full`}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <IconComponent className="h-6 w-6 mb-2" />
                  <span className="text-sm font-medium text-center">{action.label}</span>
                </button>
              </div>
            );
          })}
          {/* "Add New" placeholder tile — always visible so users discover the customise flow */}
          <button
            onClick={openAddActionDialog}
            className="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary hover:text-primary transition-all min-h-[100px]"
            data-testid="quick-action-add-tile"
            title="Add a new Quick Action"
          >
            <Plus className="h-6 w-6 mb-2" />
            <span className="text-sm font-medium">Add New</span>
          </button>
        </div>
      </CardContent>
    </Card>
  );

  const cardStyleClass = designPrefs.cardStyle === 'minimal' ? 'border-0 shadow-sm' : designPrefs.cardStyle === 'bordered' ? 'border-2' : '';
  const compactHeaderClass = designPrefs.compactMode ? 'pb-1 pt-3' : 'pb-2';
  const compactContentClass = designPrefs.compactMode ? 'pb-3' : '';

  // Phase 18.2: render one customizable KPI card (catalog-driven)
  const renderStatCardFromCatalog = (cardId, index) => {
    const def = STAT_CARDS_CATALOG[cardId];
    if (!def) return null;
    const IconComponent = AVAILABLE_ICONS[def.iconKey]?.icon
      || ({ Users, Building2, Clock, ClipboardCheck, FileText, Bell, Calendar })[def.iconKey]
      || Bell;
    const value = stats[def.metric] ?? 0;
    const formattedValue = def.format === "currency"
      ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value)
      : value;
    const isDragging = statDragIndex === index;
    return (
      <div
        key={cardId}
        className={`relative group ${isDragging ? 'opacity-40' : ''}`}
        draggable
        onDragStart={(e) => {
          setStatDragIndex(index);
          e.dataTransfer.effectAllowed = "move";
          try { e.dataTransfer.setData("text/plain", cardId); } catch { /* ignore */ }
        }}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
        onDrop={(e) => {
          e.preventDefault();
          if (statDragIndex === null || statDragIndex === index) return;
          const next = [...statCards];
          const [moved] = next.splice(statDragIndex, 1);
          next.splice(index, 0, moved);
          saveStatCards(next);
          setStatDragIndex(null);
        }}
        onDragEnd={() => setStatDragIndex(null)}
        data-testid={`stat-card-${cardId}`}
      >
        <div className="absolute -top-2 -right-2 z-10 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="secondary" size="icon" className="h-6 w-6 rounded-full shadow-md cursor-grab active:cursor-grabbing" title="Drag to reorder">
            <GripVertical className="h-3 w-3" />
          </Button>
          <Button
            variant="destructive"
            size="icon"
            className="h-6 w-6 rounded-full shadow-md"
            onClick={(e) => { e.stopPropagation(); removeStatCard(cardId); }}
            title="Hide this card"
            data-testid={`stat-card-remove-${cardId}`}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
        <Card className={`hover:shadow-md transition-shadow duration-200 ${cardStyleClass}`}>
          <CardHeader className={`flex flex-row items-center justify-between ${compactHeaderClass}`}>
            <CardTitle className="text-sm font-medium text-muted-foreground">{def.label}</CardTitle>
            <IconComponent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className={compactContentClass}>
            <div className="text-2xl font-bold font-heading">{formattedValue}</div>
            {def.description && <p className="text-xs text-muted-foreground mt-1">{def.description}</p>}
            {def.href && (
              <Link to={def.href} className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1">
                View all <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderStats = () => {
    // Only show cards that the user's role can see (defensive — prevents quoter seeing 'total_employees')
    const myRole = isOfficeManager ? "office_manager" : isQuoter ? "quoter" : "employee";
    const visible = statCards.filter((id) => {
      const def = STAT_CARDS_CATALOG[id];
      return def && def.roles.includes(myRole);
    });
    const addable = Object.values(STAT_CARDS_CATALOG)
      .filter((def) => def.roles.includes(myRole) && !visible.includes(def.id));

    return (
      <div className={`space-y-4 ${editMode ? 'ring-2 ring-primary/50 rounded-lg p-2' : ''}`} data-testid="stats-section">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-muted-foreground">Highlights</span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setAddStatDialogOpen(true)} className="text-xs gap-1 h-8" data-testid="stats-add-btn" disabled={addable.length === 0} title={addable.length === 0 ? "All available cards are already shown" : "Add a Highlight card"}>
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Add</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="stats-menu-btn" title="Customize Highlights">
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setHighlightPackDialogOpen(true)} data-testid="stats-smart-packs">
                  <Sparkles className="h-4 w-4 mr-2" /> Add Smart Pack
                </DropdownMenuItem>
                <DropdownMenuItem onClick={resetStatCards} data-testid="stats-reset">
                  <RefreshCw className="h-4 w-4 mr-2" /> Reset to Defaults
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => saveStatCards([])} data-testid="stats-hide-all">
                  <EyeOff className="h-4 w-4 mr-2" /> Hide All
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => toggleWidget('stats')} data-testid="stats-hide-widget">
                  <EyeOff className="h-4 w-4 mr-2" /> Hide Section
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {editMode && (
              <>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('stats', 'up')}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('stats', 'down')}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-muted-foreground/20 p-6 text-center text-sm text-muted-foreground">
            No Highlight cards visible. Click <strong>+ Add</strong> above to choose what to display, or pick <strong>Reset to Defaults</strong>.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {visible.map((id, idx) => renderStatCardFromCatalog(id, idx))}
          </div>
        )}
      </div>
    );
  };

  const renderRecentQuotes = () => (
    <Card className={editMode ? 'ring-2 ring-primary/50' : ''}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading">Recent Quotes</CardTitle>
        <div className="flex items-center gap-2">
          {editMode && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('recentQuotes', 'up')}>
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('recentQuotes', 'down')}>
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleWidget('recentQuotes')}>
                <EyeOff className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Link to="/quotes">
            <Button variant="ghost" size="sm">
              View all
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {recentQuotes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No quotes yet.{" "}
            {(isOfficeManager || isQuoter) && (
              <Link to="/quotes/new" className="text-primary hover:underline">
                Create your first quote
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {recentQuotes.map((quote) => (
              <Link
                key={quote.id}
                to={`/quotes/${quote.id}`}
                className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent/50 transition-colors"
                data-testid={`quote-item-${quote.id}`}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium">
                      {quote.quote_number}
                    </span>
                    <Badge variant="outline" className={getStatusClass(quote.status)}>
                      {quote.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{quote.property_address}</p>
                  <p className="text-xs text-muted-foreground">{quote.company_name}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{formatCurrency(quote.total_amount)}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(quote.date_quoted)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderUpcomingJobs = () => (
    <Card className={editMode ? 'ring-2 ring-primary/50' : ''}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading">Upcoming Jobs</CardTitle>
        <div className="flex items-center gap-2">
          {editMode && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('upcomingJobs', 'up')}>
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('upcomingJobs', 'down')}>
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleWidget('upcomingJobs')}>
                <EyeOff className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Link to="/calendar">
            <Button variant="ghost" size="sm">
              View calendar
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {upcomingJobs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No upcoming jobs this week.{" "}
            {isOfficeManager && (
              <Link to="/calendar" className="text-primary hover:underline">
                Schedule a job
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingJobs.map((job) => (
              <div
                key={job.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{job.title || job.company_name || "Job"}</p>
                    <p className="text-xs text-muted-foreground">{job.property_address || job.description || "Details TBD"}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{formatDate(job.confirmed_date || job.proposed_dates?.[0])}</p>
                  <Badge variant="outline" className="text-xs">
                    {job.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  /* ─── Calendar Widget ─── */
  const renderCalendarWidget = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();

    // Build set of days with events
    const eventDays = new Set();
    calendarEvents.forEach(ev => {
      const d = ev.confirmed_date || ev.proposed_dates?.[0];
      if (d) {
        const evDate = new Date(d);
        if (evDate.getFullYear() === year && evDate.getMonth() === month) {
          eventDays.add(evDate.getDate());
        }
      }
    });

    // Get events for today
    const todayStr = today.toISOString().split("T")[0];
    const todayEvents = calendarEvents.filter(ev => {
      const d = ev.confirmed_date || ev.proposed_dates?.[0];
      return d && d.startsWith(todayStr);
    });

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    return (
      <Card className={editMode ? 'ring-2 ring-primary/50' : ''} data-testid="calendar-widget">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Calendar
          </CardTitle>
          <div className="flex items-center gap-2">
            {editMode && (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('calendarWidget', 'up')}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('calendarWidget', 'down')}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleWidget('calendarWidget')}>
                  <EyeOff className="h-4 w-4" />
                </Button>
              </div>
            )}
            <Link to="/calendar">
              <Button variant="ghost" size="sm">
                Full view
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Mini Calendar */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-semibold">{monthNames[month]} {year}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-7 gap-0.5 text-center">
                {dayNames.map(d => (
                  <div key={d} className="text-[10px] font-medium text-muted-foreground py-1">{d}</div>
                ))}
                {Array.from({ length: firstDay }, (_, i) => (
                  <div key={`empty-${i}`} className="h-8" />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
                  const hasEvent = eventDays.has(day);
                  return (
                    <div
                      key={day}
                      className={`h-8 flex flex-col items-center justify-center rounded-md text-xs cursor-default transition-colors
                        ${isToday ? "bg-primary text-primary-foreground font-bold" : ""}
                        ${hasEvent && !isToday ? "bg-primary/10 font-medium" : ""}
                        ${!isToday && !hasEvent ? "hover:bg-accent" : ""}
                      `}
                    >
                      {day}
                      {hasEvent && <div className={`w-1 h-1 rounded-full mt-0.5 ${isToday ? "bg-primary-foreground" : "bg-primary"}`} />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Today's Events */}
            <div>
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                Today&apos;s Schedule
              </h4>
              {todayEvents.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No events today</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {todayEvents.map(ev => (
                    <div key={ev.id} className="flex items-center gap-2 p-2 rounded border text-sm">
                      <div className="w-1 h-8 rounded bg-primary shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{ev.title || "Job"}</p>
                        <p className="text-xs text-muted-foreground truncate">{ev.description || ev.property_address || ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  /* ─── Notifications Widget ─── */
  const renderNotifications = () => (
    <Card className={editMode ? 'ring-2 ring-primary/50' : ''} data-testid="notifications-widget">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notifications
          {notifications.filter(n => !n.is_read).length > 0 && (
            <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-5 flex items-center justify-center">
              {notifications.filter(n => !n.is_read).length}
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          {editMode && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('notifications', 'up')}>
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('notifications', 'down')}>
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleWidget('notifications')}>
                <EyeOff className="h-4 w-4" />
              </Button>
            </div>
          )}
          {notifications.filter(n => !n.is_read).length > 0 && (
            <Button variant="ghost" size="sm" onClick={async () => {
              try {
                await axios.post(`${API}/notifications/mark-all-read`);
                setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
              } catch {}
            }}>
              Mark all read
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {notifications.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Inbox className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No notifications</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${!notif.is_read ? "bg-primary/5 border-primary/20" : "hover:bg-accent/50"}`}
                data-testid={`notification-${notif.id}`}
              >
                <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${!notif.is_read ? "bg-primary" : "bg-muted-foreground/30"}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${!notif.is_read ? "font-medium" : ""}`}>{notif.message}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {notif.created_at ? new Date(notif.created_at).toLocaleString() : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  /* ─── Module Shortcuts Widget ─── */
  const MODULE_SHORTCUTS = [
    { label: "Email", icon: Mail, href: "/email-templates", color: "bg-blue-500/10 text-blue-600" },
    { label: "Calendar", icon: Calendar, href: "/calendar", color: "bg-emerald-500/10 text-emerald-600" },
    { label: "Clients", icon: Users, href: "/clients", color: "bg-violet-500/10 text-violet-600" },
    { label: "Quotes", icon: FileText, href: "/quotes", color: "bg-cyan-500/10 text-cyan-600" },
    { label: "Invoices", icon: Receipt, href: "/invoices", color: "bg-amber-500/10 text-amber-600" },
    { label: "Employees", icon: Users, href: "/employees", color: "bg-blue-500/10 text-blue-600" },
    { label: "Training", icon: GraduationCap, href: "/training", color: "bg-pink-500/10 text-pink-600" },
    { label: "Payroll", icon: DollarSign, href: "/payroll", color: "bg-emerald-500/10 text-emerald-600" },
    { label: "AR", icon: DollarSign, href: "/accounts-receivable", color: "bg-green-500/10 text-green-600" },
    { label: "AP", icon: DollarSign, href: "/accounts-payable", color: "bg-red-500/10 text-red-600" },
    { label: "Analytics", icon: BarChart3, href: "/analytics", color: "bg-indigo-500/10 text-indigo-600" },
    { label: "Fleet", icon: Truck, href: "/fleet", color: "bg-cyan-500/10 text-cyan-600" },
    { label: "Equipment", icon: Package, href: "/equipment", color: "bg-orange-500/10 text-orange-600" },
    { label: "Docs", icon: FolderOpen, href: "/documents", color: "bg-gray-500/10 text-gray-600" },
    { label: "Safety", icon: Shield, href: "/safety-documents", color: "bg-red-500/10 text-red-600" },
    { label: "Profile", icon: Briefcase, href: "/business-profile", color: "bg-purple-500/10 text-purple-600" },
  ];

  const renderModuleShortcuts = () => (
    <Card className={editMode ? 'ring-2 ring-primary/50' : ''} data-testid="module-shortcuts-widget">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="font-heading flex items-center gap-2">
          <ExternalLink className="h-5 w-5" />
          Module Shortcuts
        </CardTitle>
        {editMode && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('moduleShortcuts', 'up')}>
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('moduleShortcuts', 'down')}>
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleWidget('moduleShortcuts')}>
              <EyeOff className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
          {MODULE_SHORTCUTS.map(shortcut => {
            const Icon = shortcut.icon;
            return (
              <Link
                key={shortcut.label}
                to={shortcut.href}
                className="flex flex-col items-center gap-1.5 p-3 rounded-lg border hover:bg-accent/50 transition-all hover:shadow-sm group"
                data-testid={`shortcut-${shortcut.label.toLowerCase()}`}
              >
                <div className={`p-2 rounded-lg ${shortcut.color} transition-transform group-hover:scale-110`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-xs font-medium text-center leading-tight">{shortcut.label}</span>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  const renderFinancialSnapshot = () => {
    const arTotal = arSummary?.total_outstanding || 0;
    const arOverdue = arSummary?.overdue_count || 0;
    const apTotal = apSummary?.total_outstanding || 0;
    const apDueSoon = apSummary?.due_soon_count || 0;
    return (
      <Card className={editMode ? 'ring-2 ring-primary/50' : ''} data-testid="widget-financial-snapshot">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Financial Snapshot
          </CardTitle>
          <div className="flex items-center gap-2">
            {editMode && (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('financialSnapshot', 'up')}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('financialSnapshot', 'down')}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleWidget('financialSnapshot')}>
                  <EyeOff className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <Link to="/accounts-receivable" className="block p-4 rounded-lg border hover:bg-accent/50 transition-colors" data-testid="snapshot-ar">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">AR Outstanding</p>
              <p className="text-2xl font-bold">{formatCurrency(arTotal)}</p>
              <p className="text-xs text-amber-600 mt-1">
                {arOverdue} overdue invoice{arOverdue === 1 ? '' : 's'}
              </p>
            </Link>
            <Link to="/accounts-payable" className="block p-4 rounded-lg border hover:bg-accent/50 transition-colors" data-testid="snapshot-ap">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">AP Outstanding</p>
              <p className="text-2xl font-bold">{formatCurrency(apTotal)}</p>
              <p className="text-xs text-amber-600 mt-1">
                {apDueSoon} due soon
              </p>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderEmailPreview = () => (
    <Card className={editMode ? 'ring-2 ring-primary/50' : ''} data-testid="widget-email-preview">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Inbox
        </CardTitle>
        <div className="flex items-center gap-2">
          {editMode && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('emailPreview', 'up')}>
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveWidget('emailPreview', 'down')}>
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleWidget('emailPreview')}>
                <EyeOff className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Link to="/email">
            <Button variant="ghost" size="sm">Open <ArrowRight className="h-3 w-3 ml-1" /></Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-center py-6 text-muted-foreground" data-testid="email-preview-empty">
          <Mail className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm font-medium">Email is ready in the Email module</p>
          <p className="text-xs mt-1">
            Gmail / Outlook inbox sync is coming with our next release.
          </p>
          <Link to="/email">
            <Button variant="outline" size="sm" className="mt-3" data-testid="email-preview-open-btn">
              <Mail className="h-4 w-4 mr-2" />
              Open Email
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );

  const widgetRenderers = {
    quickActions: renderQuickActions,
    stats: renderStats,
    calendarWidget: renderCalendarWidget,
    recentQuotes: renderRecentQuotes,
    upcomingJobs: renderUpcomingJobs,
    notifications: renderNotifications,
    moduleShortcuts: renderModuleShortcuts,
    financialSnapshot: renderFinancialSnapshot,
    emailPreview: renderEmailPreview,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in" data-testid="dashboard">
      {/* Phase 19.36 — surfaces ?N quotes need review? when discrepancies > 20% */}
      <DiscrepancyAlert />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {designPrefs.showWelcome && (
            <>
              <h1 className="font-heading text-3xl font-bold tracking-tight">
                Welcome back, {user?.display_name || user?.name?.split(" ")[0]}
              </h1>
              <p className="text-muted-foreground mt-1">
                Here&apos;s what&apos;s happening with your business today.
              </p>
            </>
          )}
          {!designPrefs.showWelcome && (
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              Dashboard
            </h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)} data-testid="done-editing-btn">
                <Check className="h-4 w-4 mr-2" />
                Done
              </Button>
              <Button variant="ghost" onClick={resetToDefaults}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </>
          ) : (
            <>
              {/* Customization Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="customize-dropdown-btn">
                    <Settings2 className="h-4 w-4 mr-2" />
                    Customize
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => setEditMode(true)} data-testid="edit-dashboard-btn">
                    <Layout className="h-4 w-4 mr-2" />
                    Edit Dashboard Layout
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDesignDialogOpen(true)} data-testid="design-btn">
                    <Palette className="h-4 w-4 mr-2" />
                    Design & Theme
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {canPreview && (
                    <DropdownMenuItem onClick={() => setPreviewDialogOpen(true)} data-testid="preview-as-user-btn">
                      <UserCog className="h-4 w-4 mr-2" />
                      Preview as User
                    </DropdownMenuItem>
                  )}
                  {canPreview && (
                    <DropdownMenuItem onClick={() => navigate('/portal-settings')} data-testid="sidebar-settings-btn">
                      <GripVertical className="h-4 w-4 mr-2" />
                      Sidebar Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={resetToDefaults}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset to Defaults
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* "New Quote" header button removed — it's available in Quick Actions */}
            </>
          )}
        </div>
      </div>

      {/* Preview Mode Banner */}
      {previewMode && (
        <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4 flex items-center justify-between" data-testid="preview-mode-banner">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-700">Preview Mode Active</p>
              <p className="text-sm text-amber-600">
                You're viewing the app as a <span className="font-semibold capitalize">{previewMode}</span> would see it. Some features are hidden.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={exitPreview} className="border-amber-500 text-amber-700 hover:bg-amber-500/20" data-testid="exit-preview-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Exit Preview
          </Button>
        </div>
      )}

      {/* Edit Mode Banner */}
      {editMode && (
        <div className="bg-primary/10 border-2 border-primary/40 rounded-lg p-4" data-testid="edit-mode-banner">
          <div className="flex items-start gap-3">
            <Settings2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-base">Edit Mode Active</p>
              <ul className="text-sm text-foreground/80 mt-2 space-y-1 list-disc list-inside">
                <li><strong>Reorder</strong> widgets with the <ChevronUp className="inline h-3 w-3" /> / <ChevronDown className="inline h-3 w-3" /> arrows on each widget header.</li>
                <li><strong>Hide</strong> a widget with the <EyeOff className="inline h-3 w-3" /> button on its header — it&apos;ll move to the &quot;Hidden Widgets&quot; tray below.</li>
                <li><strong>Drag &amp; drop</strong> quick action tiles and stat cards to rearrange them.</li>
                <li>Click <strong>Done</strong> (top-right) to save and exit edit mode.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Hidden Widgets (show in edit mode) */}
      {editMode && Object.values(widgets).some(w => !w.enabled) && (
        <Card className="border-dashed">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <EyeOff className="h-4 w-4" />
              Hidden Widgets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.values(widgets)
                .filter(w => !w.enabled)
                .map(widget => (
                  <Button
                    key={widget.id}
                    variant="outline"
                    size="sm"
                    onClick={() => toggleWidget(widget.id)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Show {widget.name}
                  </Button>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 19.1: Asst-Mgr-only delegation widget (renders only when owner enables it) */}
      <ManagedPortalUsersWidget />

      {/* Widgets */}
      <div className="space-y-6" data-testid="widgets-container">
        {sortedWidgets
          .filter(w => w.enabled && widgetRenderers[w.id])
          .map(widget => (
            <div
              key={widget.id}
              draggable
              onDragStart={(e) => {
                setWidgetDragId(widget.id);
                e.dataTransfer.effectAllowed = "move";
                try { e.dataTransfer.setData("text/plain", widget.id); } catch { /* ignore */ }
              }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => {
                e.preventDefault();
                if (!widgetDragId || widgetDragId === widget.id) return;
                const visible = sortedWidgets.filter(w => w.enabled && widgetRenderers[w.id]);
                const fromIdx = visible.findIndex(w => w.id === widgetDragId);
                const toIdx = visible.findIndex(w => w.id === widget.id);
                if (fromIdx === -1 || toIdx === -1) return;
                // Reorder by recomputing order numbers for the dragged set
                const reordered = [...visible];
                const [moved] = reordered.splice(fromIdx, 1);
                reordered.splice(toIdx, 0, moved);
                const updated = { ...widgets };
                reordered.forEach((w, i) => { updated[w.id] = { ...updated[w.id], order: i + 1 }; });
                saveWidgets(updated);
                setWidgetDragId(null);
              }}
              onDragEnd={() => setWidgetDragId(null)}
              className={widgetDragId === widget.id ? 'opacity-40' : ''}
              data-testid={`widget-wrapper-${widget.id}`}
            >
              {widgetRenderers[widget.id]()}
            </div>
          ))}
      </div>

      {/* Design Dialog */}
      <Dialog open={designDialogOpen} onOpenChange={setDesignDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Customize Design
            </DialogTitle>
            <DialogDescription>
              Personalize your dashboard appearance
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="theme" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="theme">Theme</TabsTrigger>
              <TabsTrigger value="layout">Layout</TabsTrigger>
            </TabsList>

            <TabsContent value="theme" className="space-y-6 mt-4">
              {/* Theme Mode */}
              <div className="space-y-3">
                <Label>Theme Mode</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant={theme === "light" ? "default" : "outline"}
                    onClick={() => setTheme("light")}
                    className="flex flex-col h-auto py-3"
                  >
                    <Sun className="h-5 w-5 mb-1" />
                    <span className="text-xs">Light</span>
                  </Button>
                  <Button
                    variant={theme === "dark" ? "default" : "outline"}
                    onClick={() => setTheme("dark")}
                    className="flex flex-col h-auto py-3"
                  >
                    <Moon className="h-5 w-5 mb-1" />
                    <span className="text-xs">Dark</span>
                  </Button>
                  <Button
                    variant={theme === "system" ? "default" : "outline"}
                    onClick={() => setTheme("system")}
                    className="flex flex-col h-auto py-3"
                  >
                    <Monitor className="h-5 w-5 mb-1" />
                    <span className="text-xs">System</span>
                  </Button>
                </div>
              </div>

              {/* Accent Color */}
              <div className="space-y-3">
                <Label>Accent Color</Label>
                <div className="grid grid-cols-4 gap-2">
                  {ACCENT_COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => saveDesignPrefs({ ...designPrefs, accentColor: color.value })}
                      className={`h-12 rounded-lg ${color.class} transition-all hover:scale-105 ${
                        designPrefs.accentColor === color.value
                          ? "ring-2 ring-offset-2 ring-foreground"
                          : ""
                      }`}
                      title={color.name}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Label className="text-xs text-muted-foreground">Custom:</Label>
                  <input
                    type="color"
                    value={designPrefs.accentColor}
                    onChange={(e) => saveDesignPrefs({ ...designPrefs, accentColor: e.target.value })}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="layout" className="space-y-6 mt-4">
              {/* Card Style */}
              <div className="space-y-3">
                <Label>Card Style</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "default", label: "Default" },
                    { value: "minimal", label: "Minimal" },
                    { value: "bordered", label: "Bordered" },
                  ].map((style) => (
                    <Button
                      key={style.value}
                      variant={designPrefs.cardStyle === style.value ? "default" : "outline"}
                      onClick={() => saveDesignPrefs({ ...designPrefs, cardStyle: style.value })}
                      className="text-sm"
                    >
                      {style.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Toggle Options */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Compact Mode</Label>
                    <p className="text-xs text-muted-foreground">Reduce spacing between elements</p>
                  </div>
                  <Switch
                    checked={designPrefs.compactMode}
                    onCheckedChange={(checked) => saveDesignPrefs({ ...designPrefs, compactMode: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Show Welcome Message</Label>
                    <p className="text-xs text-muted-foreground">Display personalized greeting</p>
                  </div>
                  <Switch
                    checked={designPrefs.showWelcome}
                    onCheckedChange={(checked) => saveDesignPrefs({ ...designPrefs, showWelcome: checked })}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={resetToDefaults}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset All
            </Button>
            <Button onClick={() => setDesignDialogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Action Edit Dialog */}
      <Dialog open={quickActionDialogOpen} onOpenChange={setQuickActionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAction ? "Edit Quick Action" : "Add Quick Action"}
            </DialogTitle>
            <DialogDescription>
              Customize this quick action button
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* One-tap suggestions — only shown for new actions, not when editing */}
            {!editingAction && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Quick pick
                </Label>
                <div className="flex flex-wrap gap-1.5" data-testid="suggested-actions-strip">
                  {SUGGESTED_ACTIONS.map((s) => {
                    const SugIcon = AVAILABLE_ICONS[s.iconKey]?.icon || Plus;
                    return (
                      <button
                        type="button"
                        key={s.label}
                        onClick={() => applySuggestion(s)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-background hover:bg-accent hover:border-primary/50 transition-colors text-xs font-medium"
                        data-testid={`suggested-action-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                        title={`Use "${s.label}" as a starting point`}
                      >
                        <SugIcon className="h-3.5 w-3.5" />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Pick one to pre-fill the form, then tweak before saving — or build from scratch below.
                </p>
              </div>
            )}

            {/* Label */}
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input
                value={actionForm.label}
                onChange={(e) => setActionForm({ ...actionForm, label: e.target.value })}
                placeholder="e.g., New Quote"
                data-testid="action-label-input"
              />
            </div>

            {/* Icon */}
            <div className="space-y-2">
              <Label>Icon</Label>
              <Select
                value={actionForm.iconKey}
                onValueChange={(value) => setActionForm({ ...actionForm, iconKey: value })}
              >
                <SelectTrigger data-testid="action-icon-select">
                  <SelectValue placeholder="Select an icon" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(AVAILABLE_ICONS).map(([key, { icon: IconComp, name }]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <IconComp className="h-4 w-4" />
                        <span>{name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Destination */}
            <div className="space-y-2">
              <Label>Destination</Label>
              <Select
                value={actionForm.href}
                onValueChange={(value) => {
                  const dest = AVAILABLE_DESTINATIONS.find(d => d.href === value);
                  setActionForm({ ...actionForm, href: value, action: dest?.action || null });
                }}
              >
                <SelectTrigger data-testid="action-dest-select">
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_DESTINATIONS.map((dest) => (
                    <SelectItem key={dest.href} value={dest.href}>
                      {dest.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label>Button Color</Label>
              <div className="grid grid-cols-5 gap-2">
                {BUTTON_COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setActionForm({ ...actionForm, color: color.value })}
                    className={`h-10 rounded-lg ${color.preview} transition-all hover:scale-105 ${
                      actionForm.color === color.value
                        ? "ring-2 ring-offset-2 ring-foreground"
                        : ""
                    }`}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="flex justify-center p-4 bg-muted/50 rounded-lg">
                {(() => {
                  const PreviewIcon = AVAILABLE_ICONS[actionForm.iconKey]?.icon || Plus;
                  return (
                    <div
                      className={`flex flex-col items-center justify-center p-4 rounded-xl ${actionForm.color} text-white w-24`}
                    >
                      <PreviewIcon className="h-6 w-6 mb-2" />
                      <span className="text-sm font-medium text-center">
                        {actionForm.label || "Label"}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveActionForm} disabled={!actionForm.label.trim()} data-testid="save-action-btn">
              {editingAction ? "Save Changes" : "Add Action"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 18: Smart Starter Pack picker */}
      <Dialog open={starterPackDialogOpen} onOpenChange={setStarterPackDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Choose a Quick Actions Starter Pack
            </DialogTitle>
            <DialogDescription>
              Pick a preset bundle to instantly populate your Quick Actions. You can tweak
              individual tiles afterwards — your changes are saved automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2" data-testid="starter-packs-grid">
            {Object.values(STARTER_PACKS).map((pack) => {
              const PackIcon = AVAILABLE_ICONS[pack.iconKey]?.icon || Sparkles;
              return (
                <button
                  key={pack.id}
                  type="button"
                  onClick={() => applyStarterPack(pack.id)}
                  className="text-left rounded-xl border-2 border-muted-foreground/15 hover:border-primary hover:shadow-md transition-all p-4 bg-card"
                  data-testid={`starter-pack-${pack.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <PackIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{pack.name}</p>
                      <p className="text-xs text-muted-foreground">{pack.actions.length} tiles</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{pack.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {pack.actions.slice(0, 6).map((a, i) => {
                      const Icon = AVAILABLE_ICONS[a.iconKey]?.icon || Plus;
                      return (
                        <span
                          key={i}
                          className={`inline-flex items-center justify-center h-7 w-7 rounded-md ${a.color} text-white`}
                          title={a.label}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                      );
                    })}
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStarterPackDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 19: Persona Templates + Custom Presets picker */}
      <Dialog open={personaDialogOpen} onOpenChange={setPersonaDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layout className="h-5 w-5 text-primary" />
              Persona Templates &amp; Custom Presets
            </DialogTitle>
            <DialogDescription>
              Apply a persona to instantly set both Quick Actions and Highlight cards. Save your own layout as a Custom Preset (up to {10}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" /> Built-in Personas
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="persona-templates-grid">
                {personaTemplates.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => applyPersonaTemplate(p)}
                    className="text-left rounded-xl border-2 border-muted-foreground/15 hover:border-primary hover:shadow-md transition-all p-4 bg-card"
                    data-testid={`persona-template-${p.key}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-semibold text-sm">{p.name}</p>
                      <Badge variant="outline" className="text-[10px]">
                        {p.quick_actions?.length || 0} QA / {p.stat_cards?.length || 0} cards
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{p.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Save className="h-4 w-4 text-primary" /> My Custom Presets
                  <span className="text-xs text-muted-foreground font-normal">({customPresets.length}/10)</span>
                </h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setPersonaDialogOpen(false); setNewPresetName(""); setSavePresetDialogOpen(true); }}
                  disabled={customPresets.length >= 10}
                  data-testid="add-custom-preset-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Save current
                </Button>
              </div>
              {customPresets.length === 0 ? (
                <p className="text-xs text-muted-foreground italic" data-testid="custom-presets-list">No custom presets yet. Save your current layout to reuse it later.</p>
              ) : (
                <div className="space-y-2" data-testid="custom-presets-list">
                  {customPresets.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-2 rounded-lg border border-muted-foreground/15 p-3 bg-card"
                      data-testid={`custom-preset-${p.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.quick_actions?.length || 0} actions · {p.stat_cards?.length || 0} cards
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => applyCustomPreset(p)} data-testid={`apply-preset-${p.id}`}>
                          Apply
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteCustomPreset(p)} data-testid={`delete-preset-${p.id}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPersonaDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 19: Save preset name prompt */}
      <Dialog open={savePresetDialogOpen} onOpenChange={setSavePresetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Custom Preset</DialogTitle>
            <DialogDescription>
              Name this layout so you can reload it later. Saves the current Quick Actions and Highlight cards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="preset-name">Preset name</Label>
            <Input
              id="preset-name"
              value={newPresetName}
              onChange={(e) => setNewPresetName(e.target.value)}
              placeholder="e.g., My Sales Setup"
              maxLength={120}
              data-testid="preset-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveCurrentAsPreset} disabled={!newPresetName.trim()} data-testid="confirm-save-preset-btn">
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 18.2: Add KPI card picker */}
      <Dialog open={addStatDialogOpen} onOpenChange={setAddStatDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Add a Highlight Card
            </DialogTitle>
            <DialogDescription>
              Choose what to show in your Highlights row. Cards already on the dashboard are not listed here.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 py-2" data-testid="add-stat-grid">
            {(() => {
              const myRole = isOfficeManager ? "office_manager" : isQuoter ? "quoter" : "employee";
              const addable = Object.values(STAT_CARDS_CATALOG)
                .filter((def) => def.roles.includes(myRole) && !statCards.includes(def.id));
              if (addable.length === 0) {
                return <p className="col-span-2 text-sm text-muted-foreground text-center py-6">All available Highlight cards are already on your dashboard.</p>;
              }
              return addable.map((def) => {
                const Icon = AVAILABLE_ICONS[def.iconKey]?.icon
                  || ({ Users, Building2, Clock, ClipboardCheck, FileText, Bell, Calendar })[def.iconKey]
                  || Bell;
                return (
                  <button
                    key={def.id}
                    type="button"
                    onClick={() => addStatCard(def.id)}
                    className="text-left rounded-lg border-2 border-muted-foreground/15 hover:border-primary hover:shadow-sm transition-all p-3"
                    data-testid={`add-stat-${def.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{def.label}</p>
                        {def.description && <p className="text-xs text-muted-foreground">{def.description}</p>}
                      </div>
                    </div>
                  </button>
                );
              });
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStatDialogOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 18.3: Smart Highlights Pack picker — packs ADD to existing cards, never replace */}
      <Dialog open={highlightPackDialogOpen} onOpenChange={setHighlightPackDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Add a Smart Highlights Pack
            </DialogTitle>
            <DialogDescription>
              Each pack adds its cards to your existing Highlights — your current dashboard stays intact, and duplicates are skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2" data-testid="highlight-packs-grid">
            {Object.values(HIGHLIGHT_PACKS).map((pack) => {
              const PackIcon = AVAILABLE_ICONS[pack.iconKey]?.icon
                || ({ Users, Building2, Clock, ClipboardCheck, FileText, Bell, Truck })[pack.iconKey]
                || Sparkles;
              const myRole = isOfficeManager ? "office_manager" : isQuoter ? "quoter" : "employee";
              const newCount = pack.cards.filter((id) => {
                const def = STAT_CARDS_CATALOG[id];
                return def && def.roles.includes(myRole) && !statCards.includes(id);
              }).length;
              return (
                <button
                  key={pack.id}
                  type="button"
                  onClick={() => applyHighlightPack(pack.id)}
                  className="text-left rounded-xl border-2 border-muted-foreground/15 hover:border-primary hover:shadow-md transition-all p-4 bg-card"
                  data-testid={`highlight-pack-${pack.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <PackIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{pack.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {newCount === 0
                          ? "Already on your dashboard"
                          : `+${newCount} new card${newCount > 1 ? "s" : ""}`}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{pack.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {pack.cards.map((cardId) => {
                      const def = STAT_CARDS_CATALOG[cardId];
                      if (!def) return null;
                      const isAlreadyOn = statCards.includes(cardId);
                      return (
                        <span
                          key={cardId}
                          className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full ${
                            isAlreadyOn
                              ? "bg-muted text-muted-foreground line-through"
                              : "bg-primary/10 text-primary"
                          }`}
                          title={isAlreadyOn ? `${def.label} (already on dashboard)` : def.label}
                        >
                          {def.label}
                        </span>
                      );
                    })}
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHighlightPackDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview as User Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              Preview as User
            </DialogTitle>
            <DialogDescription>
              See how different users experience the application. The sidebar and available features will change based on the role.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Select a role to preview:
            </p>
            
            <div className="grid gap-3">
              <button
                onClick={() => {
                  startPreview("employee");
                  setPreviewDialogOpen(false);
                  toast.success("Preview mode: Employee view activated");
                }}
                className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                data-testid="preview-employee-btn"
              >
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Employee</p>
                  <p className="text-sm text-muted-foreground">
                    View schedule, safety docs, and time tracking
                  </p>
                </div>
              </button>

              <button
                onClick={() => {
                  startPreview("quoter");
                  setPreviewDialogOpen(false);
                  toast.success("Preview mode: Quoter view activated");
                }}
                className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                data-testid="preview-quoter-btn"
              >
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <FileSignature className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">Quoter</p>
                  <p className="text-sm text-muted-foreground">
                    Create quotes, manage clients, view calendar
                  </p>
                </div>
              </button>

              <button
                onClick={() => {
                  startPreview("client");
                  setPreviewDialogOpen(false);
                  toast.success("Preview mode: Client portal view activated");
                  navigate("/client-portal");
                }}
                className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                data-testid="preview-client-btn"
              >
                <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-medium">Client (Portal)</p>
                  <p className="text-sm text-muted-foreground">
                    View quotes, invoices, request callbacks
                  </p>
                </div>
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
